import { useCallback, useEffect, useRef, useState } from "react";
import { collection, listeningYearAt } from "./rotation";

const FALLBACK_SPINE = "#C8C8C8";

// Pastel tray colours shown until a cover has loaded (and under missing covers).
const TONES = [
  "#E9E4D6", "#D8DEE9", "#F1D9D0", "#DCE6D5", "#EADFF0", "#F3EAC8", "#D5E3E6",
  "#E6E6E6", "#F0D6DE", "#D9E0D0", "#E4DCCB", "#D6DCEB", "#EFE3D4", "#DFE5E5",
];
// Inverse of a hex colour, for spine text before the cover has loaded.
const inverseOf = (hex) => {
  const value = parseInt(hex.slice(1), 16);
  return `rgb(${255 - (value >> 16)}, ${255 - ((value >> 8) & 255)}, ${255 - (value & 255)})`;
};

const canHover = () => typeof window !== "undefined" && window.matchMedia("(hover: hover)").matches;

// Cases far outside the viewport (or with the shelf scrolled away) are hidden
// from rendering entirely: each one is a stack of 3D compositor layers.
const STAGE_MARGIN_PAGE = "100% 0px";
const STAGE_MARGIN_ROW = "0px 1200px";

const useOnstage = (shelfRef, viewportRef, caseRefs, count) => {
  const [shelfNear, setShelfNear] = useState(true);
  const [nearRow, setNearRow] = useState(() => new Set(Array.from({ length: count }, (_, i) => i)));

  useEffect(() => {
    if (!("IntersectionObserver" in window)) {
      return undefined;
    }

    const pageObserver = new IntersectionObserver(
      ([entry]) => setShelfNear(entry.isIntersecting),
      { rootMargin: STAGE_MARGIN_PAGE }
    );
    pageObserver.observe(shelfRef.current);

    const rowObserver = new IntersectionObserver(
      (entries) => {
        setNearRow((current) => {
          const next = new Set(current);
          entries.forEach((entry) => {
            const index = Number(entry.target.dataset.index);
            if (entry.isIntersecting) {
              next.add(index);
            } else {
              next.delete(index);
            }
          });
          return next;
        });
      },
      { root: viewportRef.current, rootMargin: STAGE_MARGIN_ROW }
    );
    caseRefs.current.forEach((element) => element && rowObserver.observe(element));

    return () => {
      pageObserver.disconnect();
      rowObserver.disconnect();
    };
  }, [caseRefs, count, shelfRef, viewportRef]);

  return (index) => shelfNear && nearRow.has(index);
};

// Dominant colour of a same-origin image, used to tint the spine and tray
// card, plus the inverse colour for the spine text. Pixels are bucketed into
// coarse colour bins and the most populated bin wins; the colour returned is
// the mean of that bin's pixels, so it's a real colour from the artwork.
const SAMPLE_SIZE = 24;
const BIN_SHIFT = 5; // 8 levels per channel
const MURKY_CHROMA = 40; // below this the colour is greyish
const WHITE_LIGHTNESS = 180; // above this a greyish colour reads as a pale tray, not murk

const dominantColor = (image) => {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = SAMPLE_SIZE;
    canvas.height = SAMPLE_SIZE;
    const context = canvas.getContext("2d");
    context.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
    const { data } = context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
    const bins = new Map();

    for (let i = 0; i < data.length; i += 4) {
      const key = ((data[i] >> BIN_SHIFT) << 6) | ((data[i + 1] >> BIN_SHIFT) << 3) | (data[i + 2] >> BIN_SHIFT);
      const bin = bins.get(key) || { count: 0, r: 0, g: 0, b: 0 };
      bin.count += 1;
      bin.r += data[i];
      bin.g += data[i + 1];
      bin.b += data[i + 2];
      bins.set(key, bin);
    }

    let best = null;
    bins.forEach((bin) => {
      if (!best || bin.count > best.count) {
        best = bin;
      }
    });

    let [red, green, blue] = [best.r, best.g, best.b].map((sum) => Math.round(sum / best.count));

    // Murky, greyish results make for a muddy tray card. Real ones are usually
    // black in that case, so snap low-chroma colours to black unless they are
    // light enough to read as white.
    const chroma = Math.max(red, green, blue) - Math.min(red, green, blue);
    const lightness = (Math.max(red, green, blue) + Math.min(red, green, blue)) / 2;

    if (chroma < MURKY_CHROMA && lightness < WHITE_LIGHTNESS) {
      [red, green, blue] = [4, 4, 4];
    }

    return {
      color: `rgb(${red}, ${green}, ${blue})`,
      text: `rgb(${255 - red}, ${255 - green}, ${255 - blue})`,
    };
  } catch {
    return { color: FALLBACK_SPINE, text: "#373737" };
  }
};

// The staggered entrance starts once the shelf has scrolled into view AND the
// first screenful of covers has loaded, so covers don't pop in after the wave.
// A timeout keeps slow connections from waiting forever.
const FIRST_WAVE = 16;
const REVEAL_TIMEOUT_MS = 1500;

const useRevealed = (shelfRef, firstWaveLoaded) => {
  const [inView, setInView] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -12% 0px" }
    );
    observer.observe(shelfRef.current);

    return () => observer.disconnect();
  }, [shelfRef]);

  useEffect(() => {
    if (!inView || firstWaveLoaded) {
      return undefined;
    }

    const timer = window.setTimeout(() => setTimedOut(true), REVEAL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [firstWaveLoaded, inView]);

  return inView && (firstWaveLoaded || timedOut);
};

// Index of the case sitting at the horizontal centre of the rack, kept up to
// date as the row scrolls, so the idle caption can show its listening year.
const useCentreIndex = (viewportRef, caseRefs) => {
  const [centre, setCentre] = useState(0);

  useEffect(() => {
    const viewport = viewportRef.current;
    let frame = null;

    const measure = () => {
      frame = null;
      const view = viewport.getBoundingClientRect();
      const middle = view.left + view.width / 2;
      let index = 0;

      caseRefs.current.forEach((element, i) => {
        if (element && element.getBoundingClientRect().left <= middle) {
          index = i;
        }
      });

      setCentre(index);
    };

    const schedule = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(measure);
      }
    };

    measure();
    viewport.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);

    return () => {
      viewport.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);

      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [caseRefs, viewportRef]);

  return centre;
};

// Travel time scales with how far the case moves across the shelf, so near
// and far cases move at a similar speed. The forward pull is the same for
// every case, so only the lateral distance counts.
const TRAVEL_BASE_MS = 420;
const TRAVEL_MS_PER_PX = 0.5;
const TRAVEL_MAX_MS = 780;
// The trip home is brisker than the trip out.
const RETURN_FACTOR = 0.7;

const travelDuration = (offset) => {
  const lateral = Math.hypot(offset.x, offset.y);
  return Math.round(Math.min(TRAVEL_MAX_MS, TRAVEL_BASE_MS + lateral * TRAVEL_MS_PER_PX));
};

const Case = ({ album, hovered, pinned, returning, dimmed, onstage, offset, caseRef, index, onEnter, onLeave, onLoaded, onToggle }) => {
  const imageRef = useRef(null);
  const tone = TONES[index % TONES.length];
  const [spine, setSpine] = useState({ color: tone, text: inverseOf(tone) });
  const [loaded, setLoaded] = useState(false);

  const handleLoad = useCallback(() => {
    const image = imageRef.current;

    if (!image) {
      return;
    }

    // Wait for the bitmap to be decoded before fading it in, otherwise the
    // first frames of the fade can paint before the pixels are ready.
    const decoded = typeof image.decode === "function" ? image.decode().catch(() => {}) : Promise.resolve();
    decoded.then(() => {
      if (imageRef.current !== image) {
        return;
      }

      setSpine(dominantColor(image));
      setLoaded(true);
      onLoaded(index);
    });
  }, [index, onLoaded]);

  useEffect(() => {
    if (imageRef.current && imageRef.current.complete && imageRef.current.naturalWidth > 0) {
      handleLoad();
    }
  }, [handleLoad]);

  const label = `${album.artist} - ${album.title}`;

  return (
    <li
      className={`case ${hovered ? "isHovered" : ""} ${pinned ? "isPinned" : ""} ${returning ? "isReturning" : ""} ${onstage || pinned || returning ? "" : "isOffstage"}`}
      data-index={index}
      ref={caseRef}
      style={{
        "--i": index,
        "--spine": spine.color,
        "--spine-text": spine.text,
        "--tone": tone,
        "--dx": `${offset.x}px`,
        "--dy": `${offset.y}px`,
        "--dz": `${offset.z}px`,
      }}
    >
      <button
        aria-hidden={dimmed}
        aria-label={label}
        aria-pressed={pinned}
        className="caseButton"
        onBlur={onLeave}
        onClick={onToggle}
        onFocus={onEnter}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        tabIndex={dimmed ? -1 : 0}
        type="button"
      >
        <span className="caseBody">
          <span className="caseFace caseFront">
            <span className="caseSpine" />
            <span className="caseCover">
              <span className="caseTray" />
              {album.cover === null ? (
                <span className="caseNoCover">
                  <span>{album.artist}</span>
                  <span>{album.title}</span>
                </span>
              ) : (
                <img
                  alt=""
                  className={loaded ? "isLoaded" : ""}
                  crossOrigin="anonymous"
                  decoding="async"
                  draggable={false}
                  fetchpriority="low"
                  onLoad={handleLoad}
                  ref={imageRef}
                  src={album.cover}
                />
              )}
            </span>
            <span className="caseLid" />
          </span>
          <span className="caseFace caseSide">
            <span className="caseSpineText">{album.artist} · {album.title}</span>
          </span>
          <span className="caseFace caseEdge" />
          <span className="caseFace caseTop" />
        </span>
        <span aria-hidden="true" className="caseHit">
          <span className="caseHitFace caseHitFront" />
          <span className="caseHitFace caseHitEdge" />
          <span className="caseHitFace caseHitTop" />
        </span>
        <span aria-hidden="true" className="caseHit caseHitLifted">
          <span className="caseHitFace caseHitFront" />
          <span className="caseHitFace caseHitEdge" />
        </span>
      </button>
    </li>
  );
};

const NO_OFFSET = { x: 0, y: 0, z: 0 };
// Chosen so the opened case appears at 1.2x under the 2300px perspective.
const PULL_DEPTH = 383;
const ROW_TILT = (3 * Math.PI) / 180; // keep in sync with .shelfRow rotateX
// Land the opened case a little above the rack's centre line.
const OPEN_RISE_PX = 8;

// Where a case must move, in the row's own 3D space, so that it projects to the
// middle of the shelf viewport once pulled forward by PULL_DEPTH.
const centeredOffset = (viewport, caseElement) => {
  const view = viewport.getBoundingClientRect();
  const box = caseElement.getBoundingClientRect();
  const style = window.getComputedStyle(viewport);
  const perspective = parseFloat(style.perspective) || 1200;
  const [originX, originY] = style.perspectiveOrigin.split(" ").map(parseFloat);
  const shrink = (perspective - PULL_DEPTH) / perspective;

  // Screen target is the viewport centre; undo the perspective projection.
  const targetX = view.left + originX + (view.width / 2 - originX) * shrink;
  // The box overhangs the rack by its negative margins (so nothing is clipped);
  // centre on the band between those overhangs.
  const overhangTop = Math.max(0, -parseFloat(style.marginTop));
  const overhangBottom = Math.max(0, -parseFloat(style.marginBottom));
  const bandHeight = view.height - overhangTop - overhangBottom;
  const targetY = view.top + originY + (overhangTop + bandHeight / 2 - OPEN_RISE_PX - originY) * shrink;
  const deltaX = targetX - (box.left + box.width / 2);
  const deltaY = targetY - (box.top + box.height / 2);

  // The row is tilted by ROW_TILT, so convert the world move into row space.
  return {
    x: deltaX,
    y: deltaY * Math.cos(ROW_TILT) + PULL_DEPTH * Math.sin(ROW_TILT),
    z: -deltaY * Math.sin(ROW_TILT) + PULL_DEPTH * Math.cos(ROW_TILT),
  };
};

const DRAG_THRESHOLD = 4;
const MOMENTUM_FRICTION = 0.94;
const MAX_GLIDE_SPEED = 48;

// Mouse drag to scroll the rack, with a little momentum on release. Touch
// devices already scroll the row natively, so this only handles mouse pointers.
const useDragScroll = (viewportRef, enabled) => {
  const dragRef = useRef({ active: false, moved: false, pointerId: null, startX: 0, startScroll: 0, velocity: 0, lastX: 0, lastTime: 0, frame: null });
  const suppressClickRef = useRef(false);
  // True from the first real movement until the glide stops: disables snapping.
  const [dragging, setDragging] = useState(false);

  const stopMomentum = useCallback(() => {
    if (dragRef.current.frame) {
      window.cancelAnimationFrame(dragRef.current.frame);
      dragRef.current.frame = null;
    }

    setDragging(false);
  }, []);

  const onPointerDown = useCallback((event) => {
    if (!enabled || event.pointerType !== "mouse" || event.button !== 0) {
      return;
    }

    const viewport = viewportRef.current;
    stopMomentum();
    dragRef.current = {
      ...dragRef.current,
      active: true,
      moved: false,
      pointerId: event.pointerId,
      startX: event.clientX,
      startScroll: viewport.scrollLeft,
      velocity: 0,
      lastX: event.clientX,
      lastTime: event.timeStamp,
    };
  }, [enabled, stopMomentum, viewportRef]);

  const onPointerMove = useCallback((event) => {
    const drag = dragRef.current;

    if (!drag.active) {
      return;
    }

    const delta = event.clientX - drag.startX;

    if (!drag.moved && Math.abs(delta) < DRAG_THRESHOLD) {
      return;
    }

    if (!drag.moved) {
      // Capture only once this is a real drag, so plain clicks still reach the cases.
      drag.moved = true;
      setDragging(true);
      viewportRef.current.setPointerCapture(drag.pointerId);
    }

    viewportRef.current.scrollLeft = drag.startScroll - delta;
    const elapsed = Math.max(event.timeStamp - drag.lastTime, 1);
    drag.velocity = (drag.lastX - event.clientX) / elapsed;
    drag.lastX = event.clientX;
    drag.lastTime = event.timeStamp;
  }, [viewportRef]);

  const onPointerUp = useCallback((event) => {
    const drag = dragRef.current;

    if (!drag.active) {
      return;
    }

    drag.active = false;

    if (!drag.moved) {
      return;
    }

    if (viewportRef.current.hasPointerCapture(event.pointerId)) {
      viewportRef.current.releasePointerCapture(event.pointerId);
    }

    // Ignore the click that fires after a drag, then glide to a stop.
    suppressClickRef.current = true;
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);

    let velocity = Math.max(-MAX_GLIDE_SPEED, Math.min(MAX_GLIDE_SPEED, drag.velocity * 16));
    const glide = () => {
      velocity *= MOMENTUM_FRICTION;

      if (Math.abs(velocity) < 0.5) {
        drag.frame = null;
        setDragging(false);
        return;
      }

      viewportRef.current.scrollLeft += velocity;
      drag.frame = window.requestAnimationFrame(glide);
    };

    drag.frame = window.requestAnimationFrame(glide);
  }, [viewportRef]);

  const onClickCapture = useCallback((event) => {
    if (suppressClickRef.current) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  useEffect(() => stopMomentum, [stopMomentum]);

  return {
    dragging,
    handlers: { onClickCapture, onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
  };
};

const Shelf = () => {
  const shelfRef = useRef(null);
  const viewportRef = useRef(null);
  const caseRefs = useRef([]);
  const [loadedCount, setLoadedCount] = useState(0);
  const loadedRef = useRef(new Set());
  const onLoaded = useCallback((index) => {
    if (index < FIRST_WAVE && !loadedRef.current.has(index)) {
      loadedRef.current.add(index);
      setLoadedCount(loadedRef.current.size);
    }
  }, []);
  const firstWaveTotal = collection.slice(0, FIRST_WAVE).filter((album) => album.cover !== null).length;
  const revealed = useRevealed(shelfRef, loadedCount >= firstWaveTotal);
  const centreIndex = useCentreIndex(viewportRef, caseRefs);
  const onstage = useOnstage(shelfRef, viewportRef, caseRefs, collection.length);
  const [hovered, setHovered] = useState(null);
  const [pinned, setPinned] = useState(null);
  const [returning, setReturning] = useState(null);
  const returnTimer = useRef(null);
  const [offset, setOffset] = useState(NO_OFFSET);
  const [travelMs, setTravelMs] = useState(TRAVEL_BASE_MS);
  const hoverEnabled = canHover();
  const { dragging, handlers: dragHandlers } = useDragScroll(viewportRef, pinned === null);
  const activeIndex = pinned ?? hovered;
  const active = activeIndex === null ? null : collection[activeIndex];

  const open = useCallback((index) => {
    const viewport = viewportRef.current;
    const caseElement = caseRefs.current[index];

    if (!viewport || !caseElement) {
      return;
    }

    const nextOffset = centeredOffset(viewport, caseElement);
    setOffset(nextOffset);
    setTravelMs(travelDuration(nextOffset));
    setPinned(index);
  }, []);

  // Mark the case as returning for the length of its trip home so it gets the
  // slower, softer transition instead of the snappy hover one.
  const close = useCallback(() => {
    setPinned((current) => {
      if (current !== null) {
        window.clearTimeout(returnTimer.current);
        setReturning(current);
        returnTimer.current = window.setTimeout(() => setReturning(null), Math.round(travelMs * RETURN_FACTOR));
      }

      return null;
    });
  }, [travelMs]);

  useEffect(() => () => window.clearTimeout(returnTimer.current), []);

  useEffect(() => {
    if (pinned === null) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        close();
      }
    };

    const handleResize = () => open(pinned);

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
    };
  }, [close, open, pinned]);

  return (
    <div className="shelf" ref={shelfRef}>
      <div
        className={`shelfViewport ${pinned === null ? "" : "isDetail"} ${dragging ? "isDragging" : ""}`}
        ref={viewportRef}
        {...dragHandlers}
      >
        <ul
          className={`shelfRow ${revealed ? "isRevealed" : ""} ${pinned === null ? "" : "hasPinned"}`}
          style={{ "--travel-ms": `${travelMs}ms`, "--return-ms": `${Math.round(travelMs * RETURN_FACTOR)}ms` }}
        >
          {collection.map((album, index) => (
            <Case
              album={album}
              caseRef={(element) => {
                caseRefs.current[index] = element;
              }}
              dimmed={pinned !== null && pinned !== index}
              hovered={hovered === index}
              index={index}
              key={`${album.artist}-${album.title}`}
              offset={pinned === index ? offset : NO_OFFSET}
              onEnter={() => hoverEnabled && setHovered(index)}
              onLeave={() => setHovered((current) => (current === index ? null : current))}
              onLoaded={onLoaded}
              onstage={onstage(index)}
              onToggle={() => (pinned === index ? close() : open(index))}
              pinned={pinned === index}
              returning={returning === index}
            />
          ))}
        </ul>
      </div>
      <p aria-live="polite" className="shelfCaption">
        <span className="shelfCaptionText">
          {active ? (
            <>
              {active.artist} - {active.title} <span>({active.genre}, {active.year})</span>
            </>
          ) : (
            <span>Listening in: {listeningYearAt(centreIndex)}</span>
          )}
        </span>
        {pinned !== null && (
          <button className="shelfBack" onClick={close} type="button">
            <svg aria-hidden="true" className="shelfBackIcon" fill="none" viewBox="0 0 16 16">
              <path
                d="M13 1.5v9H2M5.5 7 2 10.5 5.5 14"
                stroke="currentColor"
                strokeLinecap="butt"
                strokeLinejoin="miter"
                strokeWidth="1.4"
              />
            </svg>
            View all
          </button>
        )}
      </p>
    </div>
  );
};

export default Shelf;
